import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import FeatherIcon from 'feather-icons-react';
import { Link } from 'react-router-dom';
import RevealDropdown, {
  RevealDropdownTrigger
} from 'components/base/RevealDropdown';
import { useState } from 'react';
import axios from 'axios';
import { errorHandler } from 'helpers/response-utils';
import { Dropdown, Modal, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

export const patientTableColumns: ColumnDef<any>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row: { original } }) => {
      const { name, id } = original;
      return (
        <div className="d-flex align-items-center">
          <div>
            <Link to={'/clinic/patient/' + id} className="fs-8 fw-bold">
              {name}
            </Link>
          </div>
        </div>
      );
    },
    meta: {
      headerProps: {
        style: { width: 'auto' },
        className: 'ps-4 border-start border-end border-translucent ps-0'
      },
      cellProps: {
        className:
          'ps-4 border-start border-end border-translucent white-space-nowrap ps-0'
      }
    }
  },
  {
    accessorKey: 'fileID',
    header: 'File ID',
    cell: ({ row: { original } }) => {
      const { fileID } = original;
      return <>{fileID}</>;
    },
    meta: {
      headerProps: {
        style: { width: 'auto' },
        className: 'ps-4 pe-5 border-end border-translucent'
      },
      cellProps: {
        className:
          'white-space-nowrap fw-semibold ps-4 border-end border-translucent'
      }
    }
  },
  {
    accessorKey: 'email',
    header: 'Email',
    cell: ({ row: { original } }) => {
      const { email } = original;
      return (
        <Link className="text-body-highlight" to={`mailto:${email}`}>
          {email}
        </Link>
      );
    },
    meta: {
      headerProps: {
        style: { width: 'auto' },
        className: 'ps-4 pe-5 border-end border-translucent'
      },
      cellProps: {
        className:
          'white-space-nowrap fw-semibold ps-4 border-end border-translucent'
      }
    }
  },
  {
    accessorKey: 'phone',
    header: 'Phone',
    cell: ({ row: { original } }) => {
      const { phone } = original;
      return (
        <Link className="text-body-highlight" to={`tel:${phone}`}>
          {phone}
        </Link>
      );
    },
    meta: {
      headerProps: {
        style: { width: 'auto' },
        className: 'ps-4 pe-5 border-end border-translucent'
      },
      cellProps: {
        className:
          'white-space-nowrap fw-semibold ps-4 border-end border-translucent'
      }
    }
  },
  {
    accessorKey: 'dietitian',
    header: 'Dietitian',
    cell: ({ row: { original } }) => {
      const dietitian = original.dietitian;

      if (!dietitian || typeof dietitian !== 'object') {
        return <span className="text-body-tertiary">Unassigned</span>;
      }

      const dietitianName =
        [dietitian.firstname, dietitian.lastname].filter(Boolean).join(' ') ||
        dietitian.email ||
        'Unassigned';

      return <span className="white-space-nowrap">{dietitianName}</span>;
    },
    meta: {
      headerProps: {
        style: { width: 'auto' },
        className: 'ps-4 pe-5 border-end border-translucent'
      },
      cellProps: {
        className:
          'white-space-nowrap fw-semibold ps-4 border-end border-translucent'
      }
    }
  },
  {
    accessorKey: 'createdAt',
    header: 'Create date',
    cell: ({ row: { original } }) => {
      if (!original.createdAt) return 'N/A';
      return dayjs(original.createdAt).format('MMM DD, h:mm A');
    },
    meta: {
      headerProps: {
        style: { width: 'auto' },
        className: 'ps-4 pe-5'
      },
      cellProps: {
        className: 'white-space-nowrap ps-4 text-body-tertiary'
      }
    }
  },
  {
    id: 'leadDropdown',
    cell: ({ row: { original } }) => {
      const token = localStorage.getItem('token');
      const nav = useNavigate();
      const [history, setHistory] = useState<any[]>([]);
      const [historyLoading, setHistoryLoading] = useState(false);
      const [showHistory, setShowHistory] = useState(false);
      const deleteReacod = async (id: number) => {
        const response = await axios.delete(
          `${process.env.REACT_APP_ENDPOINT}/clinic/patient/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (response.data['status'] == false) {
          errorHandler(response.data);
          return false;
        }
        handleClose();
        window.location.reload();
      };

      const [show, setShow] = useState(false);

      const handleClose = () => setShow(false);
      const handleShow = () => setShow(true);
      const handleHistoryClose = () => setShowHistory(false);
      const handleShowHistory = async (id: number) => {
        setShowHistory(true);
        setHistoryLoading(true);

        try {
          const response = await axios.get(
            `${process.env.REACT_APP_ENDPOINT}/clinic/patient/${id}/dietitian-history`,
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );

          if (response.data.status === false) {
            errorHandler(response.data);
            setHistory([]);
            return;
          }

          setHistory(response.data.data.history || []);
        } catch (error) {
          setHistory([]);
          errorHandler({
            status: false,
            error: { message: 'Unable to load dietitian history.', code: 500 }
          });
        } finally {
          setHistoryLoading(false);
        }
      };
      const navigateToEdit = (id: number) => {
        nav('/clinic/patient/' + id);
      };
      return (
        <>
          <RevealDropdownTrigger>
            <RevealDropdown>
              <Dropdown.Item onClick={() => navigateToEdit(original.id)}>
                Edit
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handleShowHistory(original.id)}>
                View dietitian history
              </Dropdown.Item>
              <Dropdown.Item onClick={handleShow} className="text-danger">
                Remove
              </Dropdown.Item>
            </RevealDropdown>
          </RevealDropdownTrigger>

          <Modal
            show={show}
            onHide={handleClose}
            backdrop="static"
            keyboard={false}
          >
            <Modal.Header>
              <Modal.Title>Are you sure?</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              Are you sure you want to delete this patient?
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleClose}>
                Close
              </Button>
              <Button
                variant="primary"
                onClick={() => deleteReacod(original.id)}
              >
                Yes delete
              </Button>
            </Modal.Footer>
          </Modal>

          <Modal show={showHistory} onHide={handleHistoryClose} size="lg">
            <Modal.Header closeButton>
              <Modal.Title>Dietitian history - {original.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {historyLoading ? (
                <div className="text-center py-3">Loading history...</div>
              ) : history.length === 0 ? (
                <div className="text-center py-3">
                  No dietitian history found.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th>Dietitian</th>
                        <th>Start date</th>
                        <th>End date</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map(record => {
                        const dietitian = record.dietitian || {};
                        const dietitianName =
                          [dietitian.firstname, dietitian.lastname]
                            .filter(Boolean)
                            .join(' ') ||
                          dietitian.email ||
                          'N/A';

                        return (
                          <tr key={record.id}>
                            <td>{dietitianName}</td>
                            <td>
                              {record.start_date
                                ? dayjs(record.start_date).format(
                                    'MMM DD, YYYY'
                                  )
                                : 'N/A'}
                            </td>
                            <td>
                              {record.is_current
                                ? 'Current'
                                : record.end_date
                                ? dayjs(record.end_date).format('MMM DD, YYYY')
                                : 'N/A'}
                            </td>
                            <td>
                              {record.is_current ? 'Current' : 'Previous'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleHistoryClose}>
                Close
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      );
    },
    meta: {
      headerProps: {
        style: { width: 'auto' },
        className: 'text-end border-end pe-4 ps-4 border-translucent'
      },
      cellProps: {
        className: 'text-end border-end pe-4 ps-4 border-translucent'
      }
    }
  }
];

const PatientTable = () => {
  return (
    <div className="border-top border-translucent">
      <AdvanceTable
        tableProps={{ className: 'phoenix-table fs-9' }}
        rowClassName="hover-actions-trigger btn-reveal-trigger"
      />
      <AdvanceTableFooter pagination className="py-4" />
    </div>
  );
};

export default PatientTable;
